import { computed, effect, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { ProductStore } from './product.store';
import { UserStore } from './user.store';
import { GJData, JourneyOperationResult } from '../amplify/types';
import { GroupJourneyService } from '../service/group-journey.service';
import { UserService } from '../service/user-service';

import { withStorageSync } from '@angular-architects/ngrx-toolkit';

type GJState = {
  selectedProductId: string | null;

  selectedJourneyId: string | null;
  groupJourneys: GJData[];
  isLoading: boolean;
  reloadNext:boolean
};

const initialState: GJState = {
  selectedProductId: null,
  selectedJourneyId:null,
  groupJourneys: [],
  isLoading: false,
  reloadNext:false
};

export const GroupJourneyStore = signalStore(
  { providedIn: 'root' },

  withStorageSync({
    key: 'gj-store-synced',
    autoSync: true,
    select: ((state: GJState) => ({
      groupJourneys: state.groupJourneys,
    })) as (state: any) => Partial<any>,
  }),

  withState(initialState),

  withComputed(({ groupJourneys,selectedJourneyId }) => {
    const userStore = inject(UserStore);

    return {
      selectedJourney: computed(() => groupJourneys().find(g => g.id === selectedJourneyId()) ?? null),
      userJourneys: computed(() => {
        const user = userStore.profile();

        return groupJourneys().map(g => ({
          chats: [],
          id: g.id,
          name: g.name,
          leaderUserId: g.ownerUserId,
          leaderName: g.members?.find(m => m.userId === g.ownerUserId)?.name ?? 'Unknown Leader',
          isCurrentUserLeader: g.ownerUserId === user?.id,
          members: (g.members ?? []).map(m => ({
            userId: m.userId,
            sub: m.sub,
            name: m.name,
            avatarUrl: m.avatar,
          })),
        }));
      }),
    };
  }),

  withMethods((store, service = inject(GroupJourneyService), userService = inject(UserService), prodStore = inject(ProductStore), userStore = inject(UserStore)) => ({
    
    clearJourneys() {
      patchState(store, { isLoading: false, selectedProductId: null, groupJourneys: [], selectedJourneyId: null });
    },

    selectJourney(id:string) {
      patchState(store, { selectedJourneyId: id });
    },

    unSelectJourney() {
      patchState(store, { selectedJourneyId: null });
    },

    forceReloadNextTime() {
      patchState(store, { reloadNext: true });
    },

    async loadUserJourneys(forceReload?: boolean, productId?:string): Promise<boolean> {
      forceReload=true;
      
      if(!productId){
        const selectedProductId = prodStore.selectedProductId();
        if (!selectedProductId) {return false;}else{productId=selectedProductId;}
      }

      if(!store.reloadNext()){
        if (!forceReload && (store.isLoading() || store.selectedProductId() === productId)) {
          return false;
        }
      }else{
        console.log('Foce reload journeys!');
        patchState(store, { reloadNext: false });
      }

      patchState(store, { isLoading: true });

      try {
        const gjData = await service.loadGroupsByProductId(productId);
        // const gjData = await service.loadUserJourneys(productId);
        
        // Force reload avatars
        if (gjData) {
          gjData.forEach(g => {
            g.members?.forEach(m => {
              if (m.avatar && m.avatar.trim().length > 0) {
                m.avatar = `${m.avatar}?cb=${Date.now()}`;
              }
            });
          });
        }

        patchState(store, {
          isLoading: false,
          selectedProductId:productId,
          groupJourneys: gjData ?? [],
        });

        return !!gjData;
      } catch (error) {
        console.error('Error loading user journeys:', error);
        patchState(store, { isLoading: false });
        return false;
      }
    },

    async forceUserAvatarRefresh(avatarUrl:string){
      const currentUser = userStore.profile();
      if (!currentUser) return;

      const updatedJourneys = store.groupJourneys().map(journey => ({
        ...journey,
        members: journey.members?.map(member => ({
          ...member,
          avatar: member.userId === currentUser.id 
        ? avatarUrl 
        : member.avatar,
        })) ?? [],
      }));

      patchState(store, { groupJourneys: updatedJourneys });
    },

    async createJourney(name: string): Promise<string | null> {
      const productId = store.selectedProductId();
      if (!productId) return null;

      const userProfile = userStore.profile();
      if (!userProfile) return null;

      patchState(store, { isLoading: true });

      try {
        const newJourneyData = await service.createJourney({ name, productId });

        patchState(store, {
          isLoading: false,
          groupJourneys: [
            {
              ...newJourneyData,
              members: [
                {
                  id: userProfile.id,
                  userId: userProfile.id,
                  name: userProfile.name,
                  avatar: userProfile.avatar,
                  sub: userProfile.sub,
                },
              ],
              ownerUserId: userProfile.id,
            },
            ...store.groupJourneys(),
          ]//.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
        });

        //Refresh token, update subscriptions
        // userService.forceTokenRefresh();

        return newJourneyData.id;
      } catch (error) {
        console.error('Error creating journey:', error);
        patchState(store, { isLoading: false });
        return null;
      }
    },

    async changeJourneyName(journeyId: string, name: string): Promise<boolean> {
      const productId = store.selectedProductId();
      if (!productId) return false;

      patchState(store, { isLoading: true });

      try {
        await service.updateJourneyName({ groupId:journeyId, name:name });

        patchState(store, {
          isLoading: false,
          groupJourneys: store.groupJourneys().map(j => (j.id === journeyId ? { ...j, name } : j)),
        });

        return true;
      } catch (error) {
        console.error('Error updating journey name:', error);
        patchState(store, { isLoading: false });
        return false;
      }
    },

    async closeJourney(journeyId: string): Promise<JourneyOperationResult | null> {
      const productId = store.selectedProductId();
      if (!productId) return null;

      patchState(store, { isLoading: true });

      try {
        const result = await service.closeJourney(journeyId);

        if (result.success) {
          patchState(store, {
            isLoading: false,
            groupJourneys: store.groupJourneys().filter(j => j.id !== journeyId),
          });

          //Refresh token, update subscriptions
          // userService.forceTokenRefresh();

        } else {
          console.warn('Journey close operation failed:', result.msg);
          patchState(store, { isLoading: false });
        }


        return result;
      } catch (error) {
        console.error('Error closing journey:', error);
        patchState(store, { isLoading: false });
        return null;
      }
    },

    async exitJourney(journeyId: string, memberId: string): Promise<JourneyOperationResult | null> {
      const productId = store.selectedProductId();
      if (!productId) return null;

      patchState(store, { isLoading: true });

      try {
        const result = await service.exitJourney({ groupId: journeyId, userId: memberId });

        if (result.success) {
          patchState(store, {
            isLoading: false,
            groupJourneys: store.groupJourneys().filter(j => j.id !== journeyId),
          });

          //Refresh token, update subscriptions
          // userService.forceTokenRefresh();

        } else {
          patchState(store, { isLoading: false });
        }

        return result;
      } catch (error) {
        console.error('Error exiting journey:', error);
        patchState(store, { isLoading: false });
        return null;
      }
    },

    async removeMember(journeyId: string, memberId: string): Promise<JourneyOperationResult | null> {
      const productId = store.selectedProductId();
      if (!productId) return null;

      patchState(store, { isLoading: true });

      try {
        const result = await service.removeMember({ groupId: journeyId, userId: memberId });

        if (result.success) {
          patchState(store, {
            isLoading: false,
            groupJourneys: store.groupJourneys().map(j =>
              j.id === journeyId ? { ...j, members: (j.members ?? []).filter(m => m.userId !== memberId) } : j
            ),
          });
        } else {
          patchState(store, { isLoading: false });
        }

        return result;
      } catch (error) {
        console.error('Error removing journey member:', error);
        patchState(store, { isLoading: false });
        return null;
      }
    },

    // selectedJourney: (id: string|null) =>
    //   computed(() => id!=null?store.groupJourneys().find(g => g.id === id) ?? null:null),
  }))
);
